/**
 * CollectionEditor - Edit collection with PhotoCollection2
 *
 * This component provides a complete collection editing experience using PhotoCollection2:
 * - Photo display with grid layout
 * - Inline text editing (via CollectionEditTemplate)
 * - Multi-select bulk deletion
 * - Drag-drop reordering
 * - Upload flows (PhotoPicker, direct upload, batch drag-drop)
 *
 * Migration: Replaces custom grid + CollectionPhotoItem with PhotoCollection2 + CollectionEditTemplate (030)
 *
 * @module CollectionEditor
 */

import { Button } from '@/components/atoms';
import { useState, useEffect, useRef, useCallback } from 'react';
import { PhotoCollection2 } from '@/components/PhotoCollection2';
import { CollectionEditContext } from '@/contexts/CollectionEditContext';
import { useCollectionGraphQL } from '@/graphql/hooks/useCollectionsGraphQL';
import {
  useAddPhotoToCollectionGraphQL,
  useRemovePhotoFromCollectionGraphQL,
  useReorderCollectionPhotosGraphQL,
  useUpdateCollectionItemGraphQL,
} from '@/hooks/graphql/useCollectionMutationsGraphQL';
import { useBatchUploadGlobal } from '@/contexts/UploadContext';
import { UPLOADS_BLOCKED_MESSAGE } from '@/types/upload';
import { PhotoUploadButton } from '@/components/photos/PhotoUploadButton';
import { FileDropZone } from '@/components/photos/FileDropZone';
import logger from '@/utils/logger';
import { LoadingState, ErrorState, AddExistingPhotosModal } from '@/components/composed';
import { SelectionControls } from '@/components/composed/SelectionControls';
import { Text } from '@/components/atoms';
import { ThreePane } from '@/components/layout';
import { useLocation, useNavigate } from 'react-router-dom';

export interface CollectionEditorProps {
  collectionId: string;
  className?: string;
}

export function CollectionEditor({ collectionId, className = '' }: CollectionEditorProps) {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [showAddExistingModal, setShowAddExistingModal] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const initialFilesProcessedRef = useRef(false);
  const initialPhotoIdsProcessedRef = useRef(false);

  const { items, isLoading, error, refetch } = useCollectionGraphQL(collectionId);
  const { addPhotoToCollection } = useAddPhotoToCollectionGraphQL();
  const { removePhotoFromCollection, loading: isDeleting } = useRemovePhotoFromCollectionGraphQL();
  const { reorderCollectionPhotos } = useReorderCollectionPhotosGraphQL();
  const { updateCollectionItem } = useUpdateCollectionItemGraphQL();

  // Feature 762: Create reorder callback for defensive order verification
  // This is called after batch upload completes to ensure photos are in correct order
  const handleDefensiveReorder = useCallback(async (photoIds: string[]) => {
    // First refetch to get fresh collection state
    // This avoids using stale `items` which may not include newly uploaded photos yet
    const result = await refetch();
    // Items is a paginated result with nested items array
    const freshItems = result.data?.collection?.items?.items || [];
    const existingPhotoIds = freshItems.map((item) => item.photo?.globalId).filter(Boolean) as string[];

    // The newly uploaded photos should be appended to existing photos
    // Filter out any duplicates (in case a photo was already in collection)
    const existingSet = new Set(existingPhotoIds);
    const newPhotoIds = photoIds.filter(id => !existingSet.has(id));

    // Full order: existing photos (in their current order) + new photos (in intended order)
    const fullOrderedPhotoIds = [...existingPhotoIds, ...newPhotoIds];

    logger.info('CollectionEditor', 'Defensive reorder after batch upload', {
      existingCount: existingPhotoIds.length,
      newUploadCount: newPhotoIds.length,
      totalCount: fullOrderedPhotoIds.length
    });

    // Only call reorder if there are new photos to add
    if (newPhotoIds.length > 0) {
      await reorderCollectionPhotos({
        collectionId,
        photoIds: fullOrderedPhotoIds
      });
      // Refetch again to update UI with corrected order
      refetch();
    }
  }, [collectionId, reorderCollectionPhotos, refetch]);

  // Batch upload state for drag-drop
  // Feature 847: Use global upload state that persists across navigation
  const startingPosition = items && items.length > 0
    ? Math.max(...items.map(i => i.order || 0)) + 1
    : 1;
  const { addFiles, uploadsBlocked, sessionReady } = useBatchUploadGlobal(
    collectionId,
    startingPosition,
    {
      // Feature 762: Defensive order verification after batch completes
      reorderPhotos: handleDefensiveReorder,
      onBatchComplete: (info) => {
        logger.info('CollectionEditor', 'Batch upload completed', {
          successCount: info.successfulUploads.length,
          failedCount: info.failedCount,
          totalCount: info.totalCount
        });
        // Note: refetch is handled by handleDefensiveReorder, no need to duplicate here
      }
    }
  );

  // Message to show when uploads are blocked
  const uploadsBlockedReason = uploadsBlocked ? UPLOADS_BLOCKED_MESSAGE : undefined;

  // Handle initial files from navigation state (e.g., dropped on Create Collection button)
  // Use ref to ensure this only runs once, even if component re-renders
  // Wait for sessionReady to avoid race condition with session initialization
  useEffect(() => {
    if (initialFilesProcessedRef.current || !sessionReady) {
      return;
    }

    const locationState = location.state as { initialFiles?: File[]; initialPhotoIds?: string[] } | null;
    if (locationState?.initialFiles && locationState.initialFiles.length > 0) {
      logger.info('CollectionEditor', `Starting upload of ${locationState.initialFiles.length} initial files`);
      addFiles(locationState.initialFiles);
      initialFilesProcessedRef.current = true;

      // Clear the location state to prevent re-uploading on navigation
      // Use React Router's navigate instead of direct history manipulation
      navigate('.', { replace: true, state: {} });
    }
  }, [location.state, addFiles, navigate, sessionReady]);

  // Handle initial photo IDs from navigation state (e.g., selected from Existing Photos)
  // Use separate ref to ensure this only runs once
  useEffect(() => {
    if (initialPhotoIdsProcessedRef.current) {
      return;
    }

    const locationState = location.state as { initialPhotoIds?: string[] } | null;
    if (locationState?.initialPhotoIds && locationState.initialPhotoIds.length > 0) {
      logger.info('CollectionEditor', `Adding ${locationState.initialPhotoIds.length} existing photos`);
      initialPhotoIdsProcessedRef.current = true;

      // Add each photo to the collection
      const addPhotos = async () => {
        for (let i = 0; i < locationState.initialPhotoIds!.length; i++) {
          const photoId = locationState.initialPhotoIds![i];
          try {
            await addPhotoToCollection({
              collectionId,
              photoId,
              displayOrder: (i + 1) * 10, // Gap-based ordering
            });
          } catch (err) {
            logger.error('CollectionEditor', `Failed to add existing photo ${photoId}:`, err);
          }
        }
        // Refetch after all photos added
        refetch();
      };
      addPhotos();

      // Clear the location state to prevent re-adding on navigation
      navigate('.', { replace: true, state: {} });
    }
  }, [location.state, collectionId, addPhotoToCollection, refetch, navigate]);

  // Handle invalid file notifications
  const handleInvalidFiles = (summary: string) => {
    logger.warn('CollectionEditor', 'Invalid files dropped:', summary);
  };

  // Text update callback (via CollectionEditContext)
  const handleUpdateText = async (photoId: string, text?: string) => {
    try {
      await updateCollectionItem({
        collectionId,
        photoId,
        textContent: text,
      });
      // Don't refetch - the template maintains local state and refetching
      // would reset the textarea while user is still editing
    } catch (error) {
      logger.error('CollectionEditor', 'Failed to update text:', error);
      throw error; // Re-throw for template error handling
    }
  };

  // Add existing photos from library
  const handleAddExistingPhotos = async (photoIds: string[]) => {
    const nextPosition = items ? Math.max(...items.map(i => i.order || 0), 0) + 1 : 1;

    // Add each photo to the collection
    for (let i = 0; i < photoIds.length; i++) {
      const photoId = photoIds[i];
      try {
        await addPhotoToCollection({
          collectionId,
          photoId,
          displayOrder: nextPosition + (i * 10), // Gap-based ordering
        });
      } catch (error) {
        logger.error('CollectionEditor', `Failed to add photo ${photoId}:`, error);
      }
    }

    setShowAddExistingModal(false);
  };

  // Reorder callback (from PhotoCollection2)
  const handleReorder = async (result: any) => {
    try {
      // Extract photoIds in new order (position is inferred from array index)
      const photoIds = result.reorderedItems.map((item: any) => item.photoData.id);

      await reorderCollectionPhotos({
        collectionId,
        photoIds,
      });

      // Refetch to update UI with new order
      refetch();
    } catch (error) {
      logger.error('CollectionEditor', 'Failed to reorder photos:', error);
    }
  };

  // Bulk delete selected items
  const handleDeleteSelected = async () => {
    const count = selectedIds.length;
    if (count === 0) return;

    if (!window.confirm(`Delete ${count} photo(s) from collection?`)) {
      return;
    }

    const failedIds: string[] = [];

    // Map photo IDs to collection item IDs
    // selectedIds contains photo IDs (photo.photoData.id)
    for (const photoId of selectedIds) {
      try {
        await removePhotoFromCollection({ collectionId, photoId });
      } catch (error) {
        logger.error('CollectionEditor', `Failed to delete photo ${photoId}:`, error);
        failedIds.push(photoId);
      }
    }

    if (failedIds.length > 0) {
      // Keep failed IDs selected for retry
      setSelectedIds(failedIds);
    } else {
      // Clear selection on complete success
      setSelectedIds([]);
    }

    // Refetch to update UI
    refetch();
  };

  // Select all items
  const handleSelectAll = () => {
    const allItemIds = items?.map(item => item.photoData.id) || [];
    setSelectedIds(allItemIds);
  };

  // Clear selection
  const handleClearSelection = () => {
    setSelectedIds([]);
  };

  if (isLoading) {
    return <LoadingState message="Loading collection..." size="md" />;
  }

  if (error) {
    return (
      <ErrorState
        title="Failed to load collection"
        message={error.message || 'Could not load collection photos'}
        onRetry={() => refetch()}
      />
    );
  }

  const header = (
          <div className="flex flex-shrink-0 items-center justify-between m-4">
            <div className="flex">
              <PhotoUploadButton
                onBatchFilesSelected={addFiles}
                buttonText="Upload"
                variant="secondary"
                size="sm"
                className="mr-2"
                directMultiSelect={true}
                disabled={uploadsBlocked}
                disabledReason={uploadsBlockedReason}
              />
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setShowAddExistingModal(true)}
              >Add Existing</Button>              

            </div>

            {/* Selection controls */}
            <SelectionControls
              selectedCount={selectedIds.length}
              totalCount={items?.length || 0}
              onSelectAll={handleSelectAll}
              onClearSelection={handleClearSelection}
              onDeleteSelected={handleDeleteSelected}
              isDeleting={isDeleting}
              variant="inline"
              itemLabel="photo"
            />
          </div>);
  return (
    <CollectionEditContext.Provider value={{ onUpdateText: handleUpdateText }}>
      <FileDropZone onFilesDropped={addFiles} onInvalidFiles={handleInvalidFiles} disabled={uploadsBlocked}>
        <ThreePane className={className} header={header}>
          {items && items.length > 0 ? (
            <div className="flex-1 min-h-0">
              <PhotoCollection2              
                className='w-full h-full'
                photos={items}
                layout="grid"
                itemTemplate="collection-edit"
                zoom={1}
                selectionMode="multiple"
                selectedIds={selectedIds}
                onSelectionChange={setSelectedIds}
                reorderMode={true}
                onReorder={handleReorder}
              />
            </div>
          ) : (
            <div className="flex text-center py-8 px-4 bg-surface-1 rounded-lg mb-8">
              <Text variant="muted">No photos in collection yet. Add some below!</Text>
            </div>
          )}
        </ThreePane>
      </FileDropZone>

      {/* Feature 847: BatchUploadProgress is now rendered globally in UploadProvider */}

      {/* Add existing photos modal */}
      <AddExistingPhotosModal
        open={showAddExistingModal}
        onClose={() => setShowAddExistingModal(false)}
        onAdd={handleAddExistingPhotos}
        existingPhotoIds={items?.map(item => item.photoData.globalId || item.photoData.id) || []}
      />
    </CollectionEditContext.Provider>
  );
}
